/**
 * Task #1380 / #3006 — DTO di risposta condivisi per la vista Inventario POS.
 *
 * Single source of truth BE↔FE per:
 *   - GET /inventory/products (viste aggregated e serialized)
 *   - GET /inventory/summary
 *   - GET /inventory/products/:productId/breakdown
 *
 * I valori numerici della vista sono già normalizzati a number dal service
 * (le query usano ::float8 / ::int) e le quantità aggregate usano COALESCE a
 * zero quando lo stato corrispondente non è presente.
 */

import { z } from 'zod';
import { PRODUCT_LOGISTIC_STATUSES } from '../types/api';
import type { ProductLogisticStatus } from '../types/api';

export const inventoryViewModeSchema = z.enum(['aggregated', 'serialized']);
export type InventoryViewMode = z.infer<typeof inventoryViewModeSchema>;

export const inventorySerialModeSchema = z.enum(['none', 'unique', 'non_unique']);
export type InventorySerialMode = z.infer<typeof inventorySerialModeSchema>;

/**
 * Stato commerciale dell'unità seriale (asse commerciale di
 * `pos_product_serial_units.status`). UNICA fonte FE/BE: il CHECK a DB, il
 * motore delle transizioni backend e le META della vista inventario derivano
 * tutti da questa lista. `doa_returned` = unità ritirata dal cliente come DOA
 * (Dead On Arrival): non vendibile, non disponibile.
 */
export const INVENTORY_SERIAL_STATUSES = [
  'available',
  'reserved',
  'sold',
  'scrapped',
  'transferred',
  'doa_returned',
] as const;
export const inventorySerialStatusSchema = z.enum(INVENTORY_SERIAL_STATUSES);
export type InventorySerialStatus = z.infer<typeof inventorySerialStatusSchema>;
export function isInventorySerialStatus(value: unknown): value is InventorySerialStatus {
  return typeof value === 'string' && (INVENTORY_SERIAL_STATUSES as readonly string[]).includes(value);
}

export const inventoryStockStatusSchema = z.enum(['available', 'low', 'out']);
export type InventoryStockStatus = z.infer<typeof inventoryStockStatusSchema>;

/** Stato logistico canonico, senza reintrodurre il bucket commerciale dismesso. */
type InventoryProductLogisticStatusValue = Exclude<ProductLogisticStatus, 'sold'>;
export const inventoryProductLogisticStatusSchema = z.custom<InventoryProductLogisticStatusValue>(
  (value): value is InventoryProductLogisticStatusValue =>
    typeof value === 'string'
    && value !== 'sold'
    && PRODUCT_LOGISTIC_STATUSES.includes(value as ProductLogisticStatus),
);
export type InventoryProductLogisticStatus = z.infer<typeof inventoryProductLogisticStatusSchema>;

export const inventorySerializedSourceKindSchema = z.enum([
  'serial',
  'lot',
  'ddt',
  'manual',
  'transfer',
  'return',
  'adjustment',
  'unknown',
]);
export type InventorySerializedSourceKind = z.infer<typeof inventorySerializedSourceKindSchema>;

/** Stato della mini-distribuzione riportata nella riga aggregata. */
export const inventorySnapshotStatusDistEntrySchema = z.object({
  status: z.string(),
  quantity: z.number(),
});
export type SnapshotStatusDistEntry = z.infer<typeof inventorySnapshotStatusDistEntrySchema>;

/** Una voce della ripartizione della giacenza per magazzino. */
export const inventoryWarehouseDistributionEntrySchema = z.object({
  warehouse_id: z.string(),
  warehouse_name: z.string().nullable(),
  quantity: z.number(),
});
export type InventoryWarehouseDistributionEntry = z.infer<typeof inventoryWarehouseDistributionEntrySchema>;

/** Physical stock origins are never coalesced by a catalogue merge. */
export const inventoryOriginDetailSchema = z.object({
  stock_id: z.string(),
  product_id: z.string(),
  variant_id: z.string().nullable(),
  canonical_variant_id: z.string().nullable(),
  lot_id: z.string().nullable(),
  warehouse_id: z.string(),
  logistic_status: inventoryProductLogisticStatusSchema,
  quantity: z.number(),
});
export type InventoryOriginDetail = z.infer<typeof inventoryOriginDetailSchema>;

/**
 * Riga product-level della vista aggregata.
 *
 * Le quantità sono normalizzate dal backend a zero quando lo stato
 * corrispondente non ha righe.
 */
export const inventoryAggregatedRowSchema = z.object({
  product_id: z.string(),
  canonical_product_id: z.string().optional(),
  origin_detail: z.array(inventoryOriginDetailSchema).optional(),
  product_name: z.string(),
  sku: z.string().nullable(),
  ean: z.string().nullable(),
  category_name: z.string().nullable(),
  brand_name: z.string().nullable(),
  serial_mode: inventorySerialModeSchema,
  warehouse_count: z.number(),
  total_quantity: z.number(),
  total_reserved: z.number(),
  total_available: z.number(),
  avg_cost: z.number().nullable(),
  total_value: z.number().nullable(),
  stock_status: inventoryStockStatusSchema,
  state_distribution: z.array(inventorySnapshotStatusDistEntrySchema),
  warehouse_distribution: z.array(inventoryWarehouseDistributionEntrySchema),
  last_movement_at: z.string().nullable(),
  first_load_at: z.string().nullable(),
}).passthrough();
export type InventoryAggregatedRow = z.infer<typeof inventoryAggregatedRowSchema>;

/**
 * Riga della vista serializzata. Le quantità reserved/available sono
 * normalizzate dal backend a zero quando lo stato non è presente nel gruppo.
 */
export const inventorySerializedRowSchema = z.object({
  original_product_id: z.string().optional(),
  canonical_product_id: z.string().optional(),
  row_kind: z.enum(['serial', 'aggregate']),
  serial_id: z.string(),
  serial_number: z.string().nullable(),
  kind: z.enum(['imei', 'iccid', 'macaddr', 'serial']),
  status: inventorySerialStatusSchema.nullable(),
  serials: z.array(z.object({
    kind: z.string(),
    value: z.string(),
  }).strict()),
  product_id: z.string(),
  product_name: z.string(),
  sku: z.string().nullable(),
  ean: z.string().nullable(),
  brand_name: z.string().nullable(),
  serial_mode: inventorySerialModeSchema,
  warehouse_id: z.string().nullable(),
  warehouse_name: z.string().nullable(),
  lot_id: z.string().nullable(),
  lot_number: z.string().nullable(),
  supplier_sku: z.string().nullable(),
  logistic_status: inventoryProductLogisticStatusSchema.nullable(),
  unit_cost: z.number().nullable(),
  value: z.number().nullable(),
  quantity: z.number(),
  reserved_quantity: z.number(),
  available_quantity: z.number(),
  last_movement_at: z.string().nullable(),
  created_at: z.string().nullable(),
  carico_date: z.string().nullable(),
  ddt_number: z.string().nullable(),
  ddt_id: z.string().nullable(),
  source_kind: inventorySerializedSourceKindSchema,
}).passthrough();
export type InventorySerializedRow = z.infer<typeof inventorySerializedRowSchema>;

export const inventoryAggregatedListResponseSchema = z.object({
  mode: z.literal('aggregated'),
  identity_basis: z.literal('current_canonical').optional(),
  items: z.array(inventoryAggregatedRowSchema),
  total: z.number(),
  limit: z.number(),
  offset: z.number(),
}).passthrough();
export type InventoryAggregatedListResponse = z.infer<typeof inventoryAggregatedListResponseSchema>;

export const inventorySerializedListResponseSchema = z.object({
  mode: z.literal('serialized'),
  identity_basis: z.literal('origin_detail').optional(),
  items: z.array(inventorySerializedRowSchema),
  total: z.number(),
  limit: z.number(),
  offset: z.number(),
}).passthrough();
export type InventorySerializedListResponse = z.infer<typeof inventorySerializedListResponseSchema>;

/** KPI di testata della vista Inventario (rispetta i filtri applicati). */
export const inventorySummarySchema = z.object({
  total_products: z.number(),
  available: z.number(),
  low_stock: z.number(),
  out_of_stock: z.number(),
  /** NULL quando almeno un prodotto non ha un costo residuo determinabile. */
  total_value: z.number().nullable(),
}).passthrough();
export type InventorySummary = z.infer<typeof inventorySummarySchema>;

/** Cella sparsa della matrice magazzino × stato (solo quantity > 0). */
export const inventoryBreakdownCellSchema = z.object({
  warehouse_id: z.string(),
  logistic_status: z.string(),
  quantity: z.number(),
});
export type InventoryBreakdownCell = z.infer<typeof inventoryBreakdownCellSchema>;

/** Riga della matrice: un magazzino con il suo totale cross-stato. */
export const inventoryBreakdownWarehouseSchema = z.object({
  warehouse_id: z.string(),
  warehouse_name: z.string().nullable(),
  total: z.number(),
});
export type InventoryBreakdownWarehouse = z.infer<typeof inventoryBreakdownWarehouseSchema>;

/** Colonna della matrice: uno stato logistico con il suo totale cross-magazzino. */
export const inventoryBreakdownStateSchema = z.object({
  logistic_status: z.string(),
  total: z.number(),
});
export type InventoryBreakdownState = z.infer<typeof inventoryBreakdownStateSchema>;

/** Payload del dettaglio breakdown per-prodotto. */
export const inventoryProductBreakdownSchema = z.object({
  product_id: z.string(),
  warehouses: z.array(inventoryBreakdownWarehouseSchema),
  states: z.array(inventoryBreakdownStateSchema),
  cells: z.array(inventoryBreakdownCellSchema),
  grand_total: z.number(),
  total_available: z.number(),
  total_reserved: z.number(),
  avg_cost: z.number().nullable(),
  total_value: z.number().nullable(),
  cogs_total: z.number().nullable(),
  last_movement_at: z.string().nullable(),
}).passthrough();
export type InventoryProductBreakdown = z.infer<typeof inventoryProductBreakdownSchema>;

/**
 * Contratti della sessione di inventario fisico.
 *
 * Questi DTO intentionally espongono solo le colonne che i consumer usano:
 * il backend non deve propagare `SELECT *` e il frontend non deve ricostruire
 * la forma della sessione da una risposta implicita.
 */
export const inventorySessionStatusSchema = z.string().min(1);
export type InventorySessionStatus = z.infer<typeof inventorySessionStatusSchema>;

/**
 * Modalità di chiusura della sessione. OBBLIGATORIA nel contratto: nessun
 * default lato FE né BE (una chiusura «Completa — non contati = 0» implicita
 * azzerava giacenze non contate).
 *   - `partial`: chiude SOLO i contati; i non contati restano invariati e il
 *     lock del magazzino decade (nessuna sessione residua auto-aperta).
 *   - `complete_zero`: i non contati valgono 0 (rettifica negativa).
 *   - `complete_theoretical`: i non contati valgono il teorico (nessuna rettifica).
 */
export const INVENTORY_CLOSE_MODES = ['partial', 'complete_zero', 'complete_theoretical'] as const;
export const inventoryCloseModeSchema = z.enum(INVENTORY_CLOSE_MODES);
export type InventoryCloseMode = z.infer<typeof inventoryCloseModeSchema>;

/** POST /inventory/sessions/:id/close — body. */
export const inventorySessionCloseRequestSchema = z.object({
  mode: inventoryCloseModeSchema,
  /**
   * Con `adjustment.threshold_override`: applica subito anche le righe oltre
   * soglia invece di lasciarle in attesa di approvazione. NON esonera dal
   * motivo di rettifica sulle righe con differenza.
   */
  force: z.boolean().optional(),
}).strict();
export type InventorySessionCloseRequest = z.infer<typeof inventorySessionCloseRequestSchema>;

/**
 * Stato della rettifica di una riga di sessione.
 *   - `pending`: sessione ancora aperta / riga non ancora valutata.
 *   - `approved`: movimento applicato (o delta zero).
 *   - `pending_approval`: oltre soglia (o costo medio non determinabile):
 *     nessun movimento finché un utente con `adjustment.threshold_override`
 *     non decide.
 *   - `rejected`: decisione esplicita di NON applicare la rettifica.
 */
export const INVENTORY_ADJUSTMENT_STATUSES = ['pending', 'approved', 'pending_approval', 'rejected'] as const;
export const inventoryAdjustmentStatusSchema = z.enum(INVENTORY_ADJUSTMENT_STATUSES);
export type InventoryAdjustmentStatus = z.infer<typeof inventoryAdjustmentStatusSchema>;

/** POST /inventory/sessions/:id/items/:itemId/decision — body. */
export const inventorySessionItemDecisionRequestSchema = z.object({
  decision: z.enum(['approve', 'reject']),
  /** Motivo della decisione: obbligatorio in entrambi i casi. */
  decision_notes: z.string().trim().min(1).max(5000),
}).strict();
export type InventorySessionItemDecisionRequest = z.infer<typeof inventorySessionItemDecisionRequestSchema>;

/** POST /inventory/sessions/:id/items — body (conteggio di una riga). */
export const inventorySessionRecordCountRequestSchema = z.object({
  product_id: z.string().min(1),
  lot_id: z.string().nullable().optional(),
  quantity_counted: z.coerce.number().min(0),
  notes: z.string().max(5000).nullable().optional(),
  adjustment_reason: z.string().max(255).nullable().optional(),
  adjustment_reason_notes: z.string().max(5000).nullable().optional(),
  /**
   * Unità seriale identificata dalla scansione (IMEI/seriale). Registrata
   * come «contata» nella sessione: in chiusura le unità NON contate di un
   * articolo serializzato con differenza negativa vengono ritirate.
   */
  unit_id: z.string().nullable().optional(),
}).strict();
export type InventorySessionRecordCountRequest = z.infer<typeof inventorySessionRecordCountRequestSchema>;

/** GET /inventory/sessions/:id/lookup?code= — articolo risolto da EAN/SKU o da un seriale. */
export const inventoryLookupResponseSchema = z.object({
  id: z.string(),
  name: z.string().nullable(),
  sku: z.string().nullable(),
  ean: z.string().nullable(),
  /** Valorizzati SOLO quando il codice scansionato è un seriale (IMEI, ICCID, …). */
  serial_unit_id: z.string().nullable(),
  serial_value: z.string().nullable(),
  serial_warehouse_id: z.string().nullable(),
}).strict();
export type InventoryLookupResponse = z.infer<typeof inventoryLookupResponseSchema>;

export const inventorySessionItemRecordSchema = z.object({
  id: z.string(),
  session_id: z.string(),
  tenant_id: z.string(),
  product_id: z.string(),
  lot_id: z.string().nullable(),
  quantity_theoretical: z.number(),
  quantity_counted: z.number().nullable(),
  delta: z.number().nullable(),
  status: z.string().min(1),
  counted_at: z.string().nullable(),
  counted_by: z.string().nullable(),
  notes: z.string().nullable(),
  created_at: z.string(),
  adjustment_reason: z.string().nullable(),
  adjustment_reason_notes: z.string().nullable(),
  adjustment_status: inventoryAdjustmentStatusSchema,
  adjustment_value: z.number().nullable(),
  /**
   * Strategia «delta»: la riga ricorda il movimento `inventory` già applicato
   * e la differenza applicata. Una richiusura registra SOLO la differenza
   * rispetto a `applied_delta` (richiusura invariata ⇒ nessun movimento);
   * la riapertura NON storna.
   */
  applied_movement_id: z.string().nullable(),
  applied_delta: z.number().nullable(),
  decided_by: z.string().nullable(),
  decided_at: z.string().nullable(),
  decision_notes: z.string().nullable(),
}).passthrough();
export type InventorySessionItemRecord = z.infer<typeof inventorySessionItemRecordSchema>;

export const inventorySessionItemSchema = inventorySessionItemRecordSchema.extend({
  product_name: z.string(),
  sku: z.string().nullable(),
  ean: z.string().nullable(),
  serial_mode: inventorySerialModeSchema,
  unit_cost: z.number().nullable(),
  lot_number: z.string().nullable(),
  counted_by_name: z.string().nullable(),
  decided_by_name: z.string().nullable(),
  /** Unità seriali identificate (scansionate) per questa riga nella sessione. */
  counted_units: z.number(),
  /** Unità seriali ritirate da questa riga in chiusura/approvazione. */
  retired_units: z.number(),
});
export type InventorySessionItem = z.infer<typeof inventorySessionItemSchema>;

/** POST /inventory/sessions/:id/items/:itemId/decision — risposta. */
export const inventorySessionItemDecisionResponseSchema = inventorySessionItemRecordSchema.extend({
  /** Unità seriali ritirate dall'approvazione (0 per rifiuto o articoli non serializzati). */
  retired_units: z.number(),
});
export type InventorySessionItemDecisionResponse = z.infer<typeof inventorySessionItemDecisionResponseSchema>;

/** Columns present before Task #1380's additive session migration. */
const inventorySessionCoreSchema = z.object({
  id: z.string(),
  tenant_id: z.string(),
  warehouse_id: z.string(),
  scope_type: z.string().min(1),
  scope_filter: z.record(z.unknown()).nullable(),
  status: inventorySessionStatusSchema,
  notes: z.string().nullable(),
  opened_by: z.string().nullable(),
  closed_by: z.string().nullable(),
  opened_at: z.string(),
  closed_at: z.string().nullable(),
  close_mode: z.string().nullable(),
  created_at: z.string(),
  updated_at: z.string(),
}).passthrough();

export const inventorySessionListRowSchema = inventorySessionCoreSchema.extend({
  warehouse_name: z.string().nullable(),
  items_total: z.number(),
  items_counted: z.number(),
  /** Righe in `pending_approval` (oltre soglia, in attesa di decisione). */
  items_pending_approval: z.number(),
});
export type InventorySessionListRow = z.infer<typeof inventorySessionListRowSchema>;

/** POST /inventory/sessions/:id/close — risposta. */
export const inventorySessionCloseResponseSchema = inventorySessionCoreSchema.extend({
  /** Righe con movimento `inventory` registrato in questa chiusura. */
  adjustments: z.number(),
  /** Righe lasciate in `pending_approval` (oltre soglia o costo medio assente). */
  adjustments_pending_approval: z.number(),
  /** Righe la cui differenza era già applicata (richiusura invariata ⇒ nessun movimento). */
  adjustments_unchanged: z.number(),
  /** Unità seriali ritirate in questa chiusura. */
  retired_units: z.number(),
  threshold_value: z.number(),
});
export type InventorySessionCloseResponse = z.infer<typeof inventorySessionCloseResponseSchema>;

/** GET /inventory/sessions/:id — risposta completa della sessione. */
export const inventorySessionDetailSchema = inventorySessionCoreSchema.extend({
  blind_count: z.boolean(),
  recount_required: z.boolean(),
  approved_by: z.string().nullable(),
  approved_at: z.string().nullable(),
  warehouse_name: z.string().nullable(),
  opened_by_name: z.string().nullable(),
  closed_by_name: z.string().nullable(),
  items: z.array(inventorySessionItemSchema),
});
export type InventorySessionDetail = z.infer<typeof inventorySessionDetailSchema>;

export const inventorySessionMutationResponseSchema = inventorySessionCoreSchema.extend({
  blind_count: z.boolean(),
  recount_required: z.boolean(),
  approved_by: z.string().nullable(),
  approved_at: z.string().nullable(),
});
export type InventorySessionMutationResponse = z.infer<typeof inventorySessionMutationResponseSchema>;

/** Cancellation response deliberately excludes Task #1380 additive columns. */
export const inventorySessionCancellationResponseSchema = inventorySessionCoreSchema;
export type InventorySessionCancellationResponse = z.infer<typeof inventorySessionCancellationResponseSchema>;

/** Forma reale di `paginatedResponse` per GET /inventory/sessions. */
export const inventorySessionListResponseSchema = z.object({
  items: z.array(inventorySessionListRowSchema),
  pagination: z.object({
    page: z.number(),
    limit: z.number(),
    total: z.number(),
    totalPages: z.number(),
  }),
}).passthrough();
export type InventorySessionListResponse = z.infer<typeof inventorySessionListResponseSchema>;

/** POST /inventory/sessions — la creazione restituisce anche il conteggio snapshot. */
export const inventorySessionCreateResponseSchema = inventorySessionMutationResponseSchema.extend({
  items_total: z.number(),
});
export type InventorySessionCreateResponse = z.infer<typeof inventorySessionCreateResponseSchema>;
